import { JourneyQuestion, Recommendation } from "@/types/dataSchema";

import { Answers } from "./AnswerContext";

export const computeRecommendationKeys = (
  questions: JourneyQuestion[],
  answers: Answers,
  recommendations: Recommendation[],
  validPath?: string[],
): {
  id: string;
  excluded_by_answers: { question: string; answer: string }[];
  strong_recommendations: { question: string; answer: string }[];
  optional_recommendations: { question: string; answer: string }[];
  score: number;
}[] => {
  const recommendedRecommendations: {
    id: string;
    excluded_by_answers: { question: string; answer: string }[];
    strong_recommendations: { question: string; answer: string }[];
    optional_recommendations: { question: string; answer: string }[];
    score: number;
  }[] = recommendations.map((recommendation) => ({
    id: recommendation.id,
    excluded_by_answers: [],
    strong_recommendations: [],
    optional_recommendations: [],
    score: 0,
  }));

  if (!answers || !recommendations || recommendations.length === 0) {
    return [];
  }

  // Filter answers to only include those from the valid path
  const filteredAnswers = validPath
    ? Object.entries(answers).filter(([questionId]) =>
        validPath.includes(questionId),
      )
    : Object.entries(answers);

  filteredAnswers.forEach(([questionId, answer]) => {
    const question = questions.find((q) => q.id === questionId);

    if (!question || !question.options) {
      return;
    }
    const selectedOptions = Array.isArray(answer)
      ? question.options.filter((option) => answer.includes(option.value))
      : question.options.filter((option) => option.value === answer);

    selectedOptions.forEach((option) => {
      // Handle strong recommendations (3 points each)
      if (
        option.strong_recommendations &&
        option.strong_recommendations.length > 0
      ) {
        option.strong_recommendations.forEach((recommendationId) => {
          const recommendation = recommendedRecommendations.find(
            (s) => s.id === recommendationId,
          );

          if (recommendation) {
            recommendation.strong_recommendations.push({
              question: questionId,
              answer: option.value,
            });
            recommendation.score += 3;
          }
        });
      }

      // Handle optional recommendations (1 point each)
      if (
        option.optional_recommendations &&
        option.optional_recommendations.length > 0
      ) {
        option.optional_recommendations.forEach((recommendationId) => {
          const recommendation = recommendedRecommendations.find(
            (s) => s.id === recommendationId,
          );

          if (recommendation) {
            recommendation.optional_recommendations.push({
              question: questionId,
              answer: option.value,
            });
            recommendation.score += 1;
          }
        });
      }

      // Handle exclusions
      if (
        option.exclude_recommendations &&
        option.exclude_recommendations.length > 0
      ) {
        option.exclude_recommendations.forEach((recommendationId) => {
          const recommendation = recommendedRecommendations.find(
            (s) => s.id === recommendationId,
          );

          if (recommendation) {
            recommendation.excluded_by_answers.push({
              question: questionId,
              answer: option.value,
            });
          }
        });
      }
    });
  });

  return (
    recommendedRecommendations
      // Filter out excluded and when score is 0
      .filter((s) => s.score > 0 && s.excluded_by_answers.length === 0)
      .sort((a, b) => {
        // Sort by score (descending), then by number of exclusions (ascending)
        const scoreDiff = b.score - a.score;

        if (scoreDiff !== 0) {
          return scoreDiff;
        }

        return a.excluded_by_answers.length - b.excluded_by_answers.length;
      })
  );
};
